import { memo, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import moment from 'moment';
import { VideoAssetUsageWithUrl } from './useBulkDelete';
import Button from '@/components/atoms/Button/Button';
import IconButton from '@/components/atoms/Button/IconButton';
import { CLIP_POPOVER_ASSUME_INSIDE_CLASS_NAME } from '@/Pages/SessionDetail/constants';

function ClipBulkDeleteUsageDialog({
  isOpen,
  onClose,
  usages
}: {
  isOpen: boolean;
  onClose: () => void;
  usages: VideoAssetUsageWithUrl[];
}) {
  function visitUsage(url: string | null) {
    if (url) {
      window.open(url, '_blank');
    }
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className={`tw-relative tw-z-50 content-lab-root ${CLIP_POPOVER_ASSUME_INSIDE_CLASS_NAME}`}
        onClose={onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-white/90" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="transform duration-300 ease-in-out"
              enterFrom="translate-y-5 opacity-0"
              enterTo="translate-y-0 opacity-100"
              leave="transform duration-300 ease-in-out"
              leaveFrom="translate-y-0 opacity-100"
              leaveTo="translate-y-5 opacity-0"
            >
              <Dialog.Panel
                className="max-h-[75vh] w-full max-w-md overflow-y-auto !rounded-xl border border-slate-200 bg-white shadow-xl"
                onClick={e => e.stopPropagation()}
              >
                <div className="relative flex flex-col overflow-y-auto overflow-x-hidden">
                  <div className="grow p-5">
                    <h1 className="font-medium">Video in use</h1>
                    <div className="mt-1.5 w-full text-sm leading-tight text-slate-600">
                      Some clips could not be deleted because they are currently in use in the following destinations.
                      Please remove them from these destinations first to proceed.
                      {usages.length > 0 && (
                        <div className="mt-5">
                          {usages.map((clipUsage, index) => (
                            <div
                              key={`${clipUsage.entity_type}-${clipUsage.entity_id}-${clipUsage.entity_subtype ?? ''}-${index}`}
                              className="flex w-full items-center justify-between py-3"
                            >
                              <div>
                                <div className="text-sm font-medium text-black">{clipUsage.entity_title}</div>
                                <div className="mt-0.5 text-xs text-slate-600">{clipUsage.entity_subtype}</div>
                              </div>
                              <div className="flex flex-col items-end">
                                <IconButton
                                  icon="IconArrowUpRight"
                                  content="Visit"
                                  variation="text"
                                  size="small"
                                  trackingId="bulk-delete-usage-visit"
                                  disabled={!clipUsage.url}
                                  onClick={() => visitUsage(clipUsage.url)}
                                />
                                {clipUsage.entity_created_date && (
                                  <div className="mt-1 text-xs text-slate-600">
                                    {clipUsage.entity_type === 'EVENT' ? 'Event Date: ' : 'Created: '}
                                    {moment(clipUsage.entity_created_date).format('MMM D, YYYY')}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-3 border-t border-t-slate-200 px-5 pb-3 pt-2.5">
                    <Button variation="filled" trackingId="close-bulk-delete-usage-dialog" onClick={onClose}>
                      Close
                    </Button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default memo(ClipBulkDeleteUsageDialog);
